import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';

import {
    clearLabcodeStaffSession,
    getLabcodeStaffProfile
} from '../../lib/labcode-staff-session';
import {staffGetMe} from '../../lib/labcode-staff-api';

import {
    StaffClassesView,
    StaffDashboardView,
    StaffProjectsView,
    StaffTeachersView
} from './staff-portal-views.jsx';

import styles from './staff-portal.css';

const ROLE_LABELS = {
    platform_admin: 'Administrador geral da plataforma Labcode',
    org_admin: 'Administrador da secretaria / organização',
    school_admin: 'Direção / administração da escola',
    coordinator: 'Coordenação pedagógica',
    teacher: 'Professor'
};

function parseStaffRoute () {
    const raw = (typeof window !== 'undefined' && window.location.hash)
        ? window.location.hash.replace(/^#/, '')
        : '';
    const parts = raw.split('/').filter(Boolean);
    if (parts[0] !== 'staff') return 'dashboard';
    const page = parts[1] || 'dashboard';
    if (['dashboard', 'turmas', 'professores', 'projetos', 'editor'].includes(page)) return page;
    return 'dashboard';
}

function setStaffRoute (page) {
    window.location.hash = `staff/${page}`;
}

/**
 * Painel pós-login para equipa escolar: dashboard, turmas, projetos, editor.
 */
const StaffPortal = ({children: editorPanel}) => {
    const [route, setRoute] = useState(() => parseStaffRoute());
    const [jwtProfile] = useState(() => getLabcodeStaffProfile());
    const [staffDetail, setStaffDetail] = useState(null);
    const [meError, setMeError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { staff } = await staffGetMe();
                if (!cancelled) {
                    setStaffDetail(staff);
                    setMeError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setMeError(e && e.message ? e.message : 'Erro ao carregar perfil.');
                    setStaffDetail(null);
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onHash = () => setRoute(parseStaffRoute());
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const handleLogout = useCallback(() => {
        clearLabcodeStaffSession();
        const h = window.location.hash || '';
        if (h.startsWith('#staff') || h.startsWith('#platform')) {
            window.location.hash = '';
        }
    }, []);

    const profile = staffDetail || jwtProfile;
    const roleLabel =
        (profile && profile.role && ROLE_LABELS[profile.role]) ||
        (profile && profile.role) ||
        'Equipa escolar';

    const orgSchoolLine = staffDetail && staffDetail.organization ? (
        <span>
            {staffDetail.organization.name}
            {staffDetail.school ? ` · ${staffDetail.school.name}` : ''}
        </span>
    ) : null;

    const go = useCallback(page => {
        setStaffRoute(page);
    }, []);

    const showTeachersNav =
        profile &&
        ['coordinator', 'school_admin', 'org_admin'].includes(profile.role);

    const navBtn = (id, label) => (
        <button
            key={id}
            className={
                route === id
                    ? `${styles.navBtn} ${styles.navBtnActive}`
                    : styles.navBtn
            }
            type="button"
            onClick={() => go(id)}
        >
            {label}
        </button>
    );

    let mainContent = null;
    if (route === 'dashboard') {
        mainContent = (
            <>
                {meError ? (
                    <p
                        className={styles.errorText}
                        role="alert"
                    >
                        {meError}
                    </p>
                ) : null}
                <div className={styles.hintBox}>
                    <p>
                        <strong>{profile && profile.fullName ? profile.fullName : '—'}</strong>
                        {' · '}
                        <strong>{roleLabel}</strong>
                        {orgSchoolLine ? (
                            <>
                                <br />
                                {orgSchoolLine}
                            </>
                        ) : null}
                    </p>
                </div>
                <StaffDashboardView staffProfile={profile} />
            </>
        );
    } else if (route === 'turmas') {
        mainContent = (
            <>
                {meError ? (
                    <p
                        className={styles.errorText}
                        role="alert"
                    >
                        {meError}
                    </p>
                ) : null}
                <StaffClassesView staffProfile={profile} />
            </>
        );
    } else if (route === 'professores') {
        mainContent = (
            <>
                {meError ? (
                    <p
                        className={styles.errorText}
                        role="alert"
                    >
                        {meError}
                    </p>
                ) : null}
                {showTeachersNav ? (
                    <StaffTeachersView staffProfile={profile} />
                ) : (
                    <p className={styles.muted}>
                        {'Não tem permissão para gerir professores.'}
                    </p>
                )}
            </>
        );
    } else if (route === 'projetos') {
        mainContent = (
            <>
                {meError ? (
                    <p
                        className={styles.errorText}
                        role="alert"
                    >
                        {meError}
                    </p>
                ) : null}
                <StaffProjectsView staffProfile={profile} />
            </>
        );
    }

    return (
        <div className={styles.shell}>
            <header className={styles.topBar}>
                <p className={styles.brand}>
                    {'Labcode'}
                </p>
                <span className={styles.userMeta}>
                    {profile && profile.email ? profile.email : ''}
                </span>
                <button
                    className={styles.logoutBtn}
                    type="button"
                    onClick={handleLogout}
                >
                    {'Sair'}
                </button>
            </header>
            <div className={styles.body}>
                <nav
                    aria-label="Área equipa escolar"
                    className={styles.sideNav}
                >
                    {navBtn('dashboard', 'Dashboard')}
                    {navBtn('turmas', 'Turmas e alunos')}
                    {showTeachersNav ? navBtn('professores', 'Professores') : null}
                    {navBtn('projetos', 'Projetos')}
                    {navBtn('editor', 'Editor Labcode')}
                </nav>
                <main
                    className={route === 'editor' ? styles.mainEditor : styles.main}
                >
                    {route === 'editor' ? (
                        <div className={styles.editorFill}>
                            {editorPanel}
                        </div>
                    ) : (
                        mainContent
                    )}
                </main>
            </div>
        </div>
    );
};

StaffPortal.propTypes = {
    children: PropTypes.node
};

StaffPortal.defaultProps = {
    children: null
};

export default StaffPortal;
